import React, { useEffect, useRef } from 'react';
import useMedicalProfiles from '../../../../hooks/api/useMedicalProfiles';
import Avatar, { getFallbackFromName } from '../../../v2BaseComponents/Avatar';
import { useAvatar } from '../../../../hooks/useAvatar';
import urls from '../../../../utils/urls';
import { useNestedNavigation, useTabRouteActiveProfile } from '../../../../utils/appNavigationNames';
import NavigationItem from './NavigationItem';
import { APITypes } from '../../../../primary-record-types';
import { getIntl } from '../../../../utils/intl';
import Text from '../../../v2BaseComponents/Text';
import Stack from '../../../v2BaseComponents/Stack';
import { useGetIsUserProfile } from '../../../../hooks/useUserHasOwnMedicalProfile';
import { COLLAPSE_ANIMATION_DURATION, WIDTHS } from './constants';
import { Animated, Platform } from 'react-native';
import { colors } from '../../../../utils/theme';
import { useIsMobileLayout } from '../../../../hooks/useIsMobileLayout';
import { useProfileUnreadInAppNotificationCount } from '../../../../hooks/api/useProfileInUnreadAppNotificationCount';
import NotificationCountIndicator from '../../../v2BaseComponents/NotificationIndicator/NotificationCountIndicator';

const intl = getIntl('profile-navigation-items', {
    sharedProfiles: 'Shared Profiles',
    me: `{name} (me)`,
    profilesYouManage: 'Profiles You Manage',
    myProfile: 'My Profile',
});

export type ProfileNavigationItemsOwnProps = {
    isCollapsed: boolean;
};

export type ProfileNavigationItemsProps = ProfileNavigationItemsOwnProps;

const getProfileDisplay = (profile: APITypes.medicalProfileService.ProfileListItem) => {
    return profile.preferredFirstName || profile.firstName || '';
};

const sortProfiles = (profiles: APITypes.medicalProfileService.ProfileListItem[]) => {
    return [...(profiles || [])].sort((a, b) => {
        return getProfileDisplay(a).localeCompare(getProfileDisplay(b));
    });
};

export const ProfileNavigationItem: React.FC<{
    profile: APITypes.medicalProfileService.ProfileListItem;
    isCollapsed: boolean;
}> = (props) => {
    const isMobile = useIsMobileLayout();
    const isUserProfile = useGetIsUserProfile()(props.profile);
    const { nestedProfileNavigation } = useNestedNavigation();
    const routeActiveProfile = useTabRouteActiveProfile();
    const { avatar, isLoadingAvatar } = useAvatar({
        avatarAPIUrl: urls.api.medicalProfiles.avatar(props.profile.medicalProfileId),
    });
    const notificationCount = useProfileUnreadInAppNotificationCount({
        medicalProfileId: props.profile.medicalProfileId,
    });
    const display = getProfileDisplay(props.profile);
    const icon = (
        <Avatar
            size="drawer-avatar"
            image={avatar?.base64 || null}
            isLoading={isLoadingAvatar}
            fallbackName={getFallbackFromName(
                props.profile.preferredFirstName || props.profile.firstName,
                props.profile.lastName,
            )}
        />
    );

    return (
        <NavigationItem
            icon={icon}
            isLargeIcon
            display={isUserProfile ? intl('myProfile') : display}
            isActive={routeActiveProfile?.profileId === props.profile.medicalProfileId}
            isCollapsed={props.isCollapsed}
            onPress={() => {
                if (isMobile) {
                    nestedProfileNavigation('profile', {
                        firstName: props.profile.firstName || '',
                        profileId: props.profile.medicalProfileId,
                    });
                } else {
                    nestedProfileNavigation('profile-activity', {
                        profileId: props.profile.medicalProfileId,
                    });
                }
            }}
            testID={`profile-navigation-item-${display}`}
            smallFont
            rightContent={<NotificationCountIndicator count={notificationCount.unreadInAppNotificationCount} />}
        />
    );
};

const DIVIDER_HEIGHT = 33;

const ProfileNavigationItems: React.FC<ProfileNavigationItemsProps> = (props) => {
    const opacityAnimation = useRef(new Animated.Value(props.isCollapsed ? 0 : 1));
    const widthAnimation = useRef(new Animated.Value(props.isCollapsed ? WIDTHS.collapsed : WIDTHS.expanded));
    useEffect(() => {
        if (!opacityAnimation.current || !widthAnimation.current) return;

        Animated.timing(opacityAnimation.current, {
            toValue: props.isCollapsed ? 0 : 1,
            duration: COLLAPSE_ANIMATION_DURATION,
            useNativeDriver: Platform.OS === 'web',
        }).start();

        Animated.timing(widthAnimation.current, {
            toValue: props.isCollapsed ? WIDTHS.collapsed : WIDTHS.expanded,
            duration: COLLAPSE_ANIMATION_DURATION,
            useNativeDriver: Platform.OS === 'web',
        }).start();
    }, [props.isCollapsed]);

    const isUserProfile = useGetIsUserProfile();

    const { collaboratorMedicalProfiles, medicalProfiles } = useMedicalProfiles();

    const nonUserMedicalProfiles = medicalProfiles.filter((profile) => !isUserProfile(profile));

    const renderMessageSection = (message: string) => (
        <Animated.View
            style={{
                width: widthAnimation.current,
                height: DIVIDER_HEIGHT,
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: colors['Drawer-Button'],
                paddingLeft: 32,
                overflow: 'hidden',
            }}
            collapsable={false}
        >
            <Animated.View style={{ opacity: opacityAnimation.current }}>
                <Text variant="body-2" wrapText={false} color="Primary-Light">
                    {message}
                </Text>
            </Animated.View>
        </Animated.View>
    );

    return (
        <Stack space={12} vertical style={{ width: '100%' }}>
            <Stack space={4} vertical style={{ width: '100%' }}>
                {nonUserMedicalProfiles.length ? renderMessageSection(intl('profilesYouManage')) : null}
                {sortProfiles(nonUserMedicalProfiles).map((profile) => (
                    <ProfileNavigationItem
                        isCollapsed={props.isCollapsed}
                        key={profile.medicalProfileId}
                        profile={profile}
                    />
                ))}
            </Stack>
            {collaboratorMedicalProfiles.length ? (
                <Stack space={4} vertical style={{ width: '100%' }}>
                    {renderMessageSection(intl('sharedProfiles'))}
                    {sortProfiles(collaboratorMedicalProfiles).map((profile) => (
                        <ProfileNavigationItem
                            isCollapsed={props.isCollapsed}
                            key={profile.medicalProfileId}
                            profile={profile}
                        />
                    ))}
                </Stack>
            ) : null}
        </Stack>
    );
};

export default React.memo(ProfileNavigationItems);
